import React, { useContext, useEffect, useState } from "react";
import { MainContextState, MainContext } from "../../context/MainContext";
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNotification } from "../../hooks/useNotification";
import Loader from "../../components/Loader/Loader";
import CustomSnackbar from "../../components/UI/CustomSnackbar";
import { MODAL_TYPE } from "../../constants/common";
import {
    createNewReportControlPoint,
    deleteReportControlPoint,
    getReportControlPoint,
    getReportControlPoints,
} from "../../api/reportControlPointAPI";
import { ReportControlPointData, ReportControlPointCreate } from "../../types/api/report";
import Modal from "../../components/UI/Modal/Modal";
import { CreateForm } from "./Modals/CreateForm";
import { getDateFormat } from "../../utils/getDateFormat";

export const ReportControlPointsPage = () => {
    const isAdmin = localStorage.getItem("admin_role") === "true";
    const { constants } = useContext<Partial<MainContextState>>(MainContext);
    const reportTypes = constants?.export_type?.filter(item => item.text.includes("Форма"));

    const [load, setLoad] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const [points, setPoints] = useState<Array<ReportControlPointData>>();
    const [pointId, setPointId] = useState(0);
    const [deletePointId, setDeletePointId] = useState(0);
    const [type, setType] = useState<string>("");
    const [date, setDate] = useState<string>("");
    const { setNotification, setOpenSnackbar, notification, openSnackbar } = useNotification();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentModalType, setCurrentModalType] = useState("edit");
    const closeModal = () => {
        setShowModal(false);
    };
    const createForm = useForm<ReportControlPointCreate>({
        defaultValues: {
            name: "",
            report_type: undefined,
            description: "",
            expired_at: undefined,
        },
    });

    const fetchPoints = (type: string, date: string) => {
        setLoad(true);
        getReportControlPoints(
            ({ data, last_page, current_page }) => {
                setPoints(data);
                setPagination({ ...pagination, last_page: last_page, current_page: current_page });
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
            {
                page: pagination.current_page,
                ...(type ? { report_type: type } : {}),
                ...(date ? { from: date } : {}),
            },
        );
    };

    const getPointData = (pointId: number) => {
        setLoad(true);
        getReportControlPoint(
            pointId,
            () => {
                setLoad(false);
                setShowModal(true);
            },
            err => {
                setLoad(false);
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    };

    const create: SubmitHandler<ReportControlPointCreate> = (data: any) => {
        setLoad(true);
        createNewReportControlPoint(
            data,
            () => {
                fetchPoints(type, date);
                setNotification({
                    severity: "success",
                    message: "Контрольная точка добавлена успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
                if (err?.errors) {
                    Object.keys(err.errors).forEach((fieldName: any) => {
                        createForm.setError(fieldName, {
                            type: "custom",
                            message: err.errors[fieldName][0],
                        });
                    });
                }
            },
        );
    };

    const del: SubmitHandler<any> = () => {
        setLoad(true);
        deleteReportControlPoint(
            deletePointId,
            () => {
                fetchPoints(type, date);
                setNotification({
                    severity: "success",
                    message: "Центр удален успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const createContent = (
        <FormProvider {...createForm}>
            <CreateForm />
        </FormProvider>
    );

    const deleteContent = <div>Вы уверены, что хотите удалить данные о контрольной точке?</div>;
    const modalType = {
        [MODAL_TYPE.create]: {
            title: "Создание контрольной точки",
            onSubmit: createForm.handleSubmit(create),
            content: createContent,
        },

        [MODAL_TYPE.delete]: {
            title: "Удаление  контрольной точки",
            onSubmit: del,
            content: deleteContent,
        },
    };

    useEffect(() => {
        setLoad(true);
        fetchPoints(type, date);
    }, [pagination.current_page, type, date]);

    useEffect(() => {
        pointId && getPointData(pointId);
    }, [pointId]);

    return (
        <div className={"container"}>
            <div className={"pageHeader"}>Контрольные точки</div>
            <Stack direction={"row"} gap={"10px"} mb={1}>
                <FormControl
                    sx={{
                        width: "360px",
                    }}>
                    <InputLabel id={"resident-type-label"}>Тип отчета</InputLabel>
                    <Select
                        size={"small"}
                        labelId={"resident-type"}
                        label={"Тип резидента"}
                        onChange={(event: SelectChangeEvent) => {
                            setType(event.target.value);
                        }}
                        value={type}>
                        {reportTypes?.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.text}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size={"small"}
                    type="date"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                    label="Дата"
                    onChange={(e: any) => {
                        setDate(e.target.value.replace(/-/g, "/"));
                    }}
                    value={date?.replace(/\//g, "-")}
                    autoComplete="off"
                />
            </Stack>
            {/* <Search
                clearHandler={value => {
                    setQuery(value);
                }}
                searchHandler={value => {
                    setQuery(value);
                }}
            /> */}
            <TableContainer component={Paper} sx={{ marginBottom: "80px" }}>
                <Table aria-label="centers table" sx={{ "& td": { border: "none" } }}>
                    <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                        <TableRow>
                            <TableCell>Наименование</TableCell>
                            <TableCell align="center">Описание</TableCell>
                            <TableCell align="center">Форма</TableCell>
                            <TableCell align="center">Дата</TableCell>
                            <TableCell align="center"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {points?.map(point => (
                            <TableRow key={point.id} sx={{ border: 0 }}>
                                <TableCell align="center">{point.name}</TableCell>
                                <TableCell align="center">{point.description}</TableCell>
                                <TableCell align="center">
                                    {
                                        constants?.export_type.find(state => state.value === Number(point.report_type))
                                            ?.text
                                    }
                                </TableCell>
                                <TableCell align="center">{getDateFormat(point.expired_at)}</TableCell>
                                <TableCell align="center">
                                    {isAdmin && (
                                        <div className={"btnContainer"}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowModal(true);
                                                    setDeletePointId(point.id);
                                                    setCurrentModalType(MODAL_TYPE.delete);
                                                }}>
                                                Удалить
                                            </Button>
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className={"posBR"}>
                <div className={"paginationContainer"}>
                    <Pagination
                        onChange={(index, qwerty) => setPagination({ ...pagination, current_page: qwerty })}
                        page={pagination.current_page}
                        count={pagination.last_page}
                        shape="rounded"
                    />
                </div>
                {isAdmin && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            createForm.reset();
                            setCurrentModalType(MODAL_TYPE.create);
                            setShowModal(true);
                        }}>
                        Добавить
                    </Button>
                )}
            </div>
            <Modal
                open={showModal}
                title={modalType[currentModalType]?.title}
                onClose={() => {
                    closeModal();
                }}
                onSubmit={modalType[currentModalType]?.onSubmit}>
                {modalType[currentModalType]?.content}
            </Modal>
            <CustomSnackbar openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} notification={notification} />
            <Loader backDrop open={load} />
        </div>
    );
};
